import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/** How long after arriving the page is expected to go on changing height. */
const SETTLE_MS = 6000;
/** How far off its mark a section may sit before it is put back. */
const TOLERANCE_PX = 4;
/** What a reader does to say they are scrolling for themselves now. */
const READER_EVENTS = ["wheel", "touchstart", "keydown", "pointerdown"] as const;

/**
 * Scroll to the section named in the URL.
 *
 * Every content section carries an anchor so a link can point at one part of
 * the page: sending someone "the funds bit" should land them on the funds bit,
 * not at the top with instructions to scroll. Clicking a nav item already
 * scrolls; this is the other half, for a URL that arrives with a hash on it,
 * whether typed, shared or followed from an email.
 *
 * The section may not be on the page the moment this runs, since parts of it
 * wait on data, so the target is looked for a few times before giving up
 * rather than once at mount.
 *
 * Finding it is not the end either. A page that has just loaded goes on
 * growing above the section, as faces, lists and the graph arrive, and each
 * arrival pushes the section down and leaves the reader looking at the one
 * before it. So for a few seconds after arriving the section is put back on
 * its mark whenever the page changes height, and that stops the moment the
 * reader scrolls, taps or types, since from then on where the page sits is
 * theirs to decide.
 *
 * Arriving and moving are shown differently. A page that loads on a section
 * is simply there, with no scroll to watch: the reader asked for the section,
 * not for a tour of what is above it. Once on the page, choosing another
 * section scrolls to it, so it is plain that the page moved and which way.
 */
export function useAnchorScroll({
  attempts = 10,
  intervalMs = 150,
}: { attempts?: number; intervalMs?: number } = {}) {
  const { hash } = useLocation();
  // The first run is the page arriving; every later one is a section chosen
  // from a page already open.
  const hasArrived = useRef(false);

  useEffect(() => {
    const isArriving = !hasArrived.current;
    hasArrived.current = true;
    if (!hash) return;

    const targetId = decodeURIComponent(hash.replace(/^#/, ""));
    if (!targetId) return;

    let remaining = attempts;
    let timer: ReturnType<typeof setTimeout>;
    let settleTimer: ReturnType<typeof setTimeout>;
    let observer: ResizeObserver | undefined;

    const stopHolding = () => {
      observer?.disconnect();
      observer = undefined;
      clearTimeout(settleTimer);
      READER_EVENTS.forEach((type) => window.removeEventListener(type, stopHolding));
    };

    const hold = (element: HTMLElement) => {
      if (typeof ResizeObserver === "undefined") return;
      observer = new ResizeObserver(() => {
        const mark = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
        const offBy = element.getBoundingClientRect().top - mark;
        const atPageEnd =
          window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;
        if (Math.abs(offBy) <= TOLERANCE_PX || (offBy > 0 && atPageEnd)) return;
        element.scrollIntoView({ behavior: "auto", block: "start" });
      });
      observer.observe(document.body);
      READER_EVENTS.forEach((type) =>
        window.addEventListener(type, stopHolding, { passive: true })
      );
      settleTimer = setTimeout(stopHolding, SETTLE_MS);
    };

    const attempt = () => {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({
          behavior: isArriving ? "auto" : "smooth",
          block: "start",
        });
        // Holding is for a page still loading. A page already open has
        // settled, and putting the section back on its mark the moment the
        // scroll begins would cut the scroll short and make it a jump.
        if (isArriving) hold(element);
        return;
      }
      remaining -= 1;
      if (remaining > 0) {
        timer = setTimeout(attempt, intervalMs);
      }
    };

    attempt();
    return () => {
      clearTimeout(timer);
      stopHolding();
    };
  }, [hash, attempts, intervalMs]);
}

export default useAnchorScroll;
